import { Button, Link, Typography } from "@material-ui/core"
import { postEcommOrders, putEcommOrders } from "api"
import FormikCheckboxField from "common/FormikCheckboxField"
import UseFocusFirstInvalidField from "common/UseFocusFirstInvalidField"
import { trackCheckoutStep, trackPersonalDetails } from "common/analytics"
import useRedirectIfPreviousComponentsAreNotSelected from "common/useRedirectIfPreviousComponentsAreNotSelected"
import ShowRoomSelect from "components/shopFeatures/auth/register/ShowRoomSelect"
import {
  fetchPaymentUrl,
  setOutOfStockComplete,
} from "components/shopFeatures/payment/paymentSlice"
import { Field, Form, Formik, FormikProps } from "formik"
import { TextField } from "formik-material-ui"
import { useRouter } from "next/router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useHistory } from "react-router-dom"
import routes from "routes"
import { useAppDispatch, useTypedSelector } from "store"
import styled from "styled-components"
import * as yup from "yup"
import { Font } from "../wizard/constants"
import useInStock from "../wizard/useInStock"
import * as orderRepository from "./orderRepository"

const StyledHeading = styled(Typography).attrs({ variant: "h2" })`
  text-align: center;
  font-size: 24px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 30px;
  }
`
const StyledForm = styled(Form)`
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 30px;
    margin-bottom: 30px;
  }

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 75vh;
`
const StyledButton = styled(Button)<{ $disabled: boolean }>`
  margin-top: 30px;
  background: black;
  color: white;
  cursor: pointer;

  :hover,
  :focus {
    background: ${(p) => p.theme.colors.steel};
    border: none;
    color: white;
    outline: none;
  }

  ${(p) =>
    p.$disabled &&
    `
    background: ${p.theme.colors.darkGrey};
  `}
`

const StyledField = styled(Field)<{ $email?: boolean }>`
  label {
    font-family: ${Font.OENaPro};
    font-size: 12px;
    font-weight: 300;
    line-height: 16px;
    letter-spacing: 0em;
  }

  ${(p) =>
    p?.$email &&
    `
  input {
    font-family: ${Font.MazdaType};
    letter-spacing: 0;
    font-size: 14px;
  }
  `}
`

const StyledRow = styled.div`
  display: flex;
  gap: 10px;
  > * {
    width: 50%;
  }
`

const StyledHeadingContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
`

const StyledImageButton = styled(Button)`
  position: absolute;
  right: 0px;
  top: 24px;
  padding: 0;
  margin: 0;
  min-width: 13px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    top: 38px;
  }
`

const StyledFormContainer = styled.div``

const StyledInnerContainer = styled.div`
  padding-top: 20px;
`

const StyledImage = styled.img``

const StyledInfo = styled(Typography)`
  font-family: ${Font.OENaPro};
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: right;
  color: ${(p) => p.theme.colors.mazdaRed};
  margin: 10px 0;
`

const schema = yup
  .object({
    firstName: yup
      .string()
      .required("שדה חובה")
      .max(50, "ניתן להזין עד 50 תווים")
      .matches(/^[a-zA-Zא-ת ]+$/, "שם פרטי בבקשה"),
    lastName: yup
      .string()
      .required("שדה חובה")
      .max(50, "ניתן להזין עד 50 תווים")
      .matches(/^[a-zA-Zא-ת ]+$/, "שם משפחה בבקשה"),
    mobilePhone: yup
      .string()
      .matches(/^05\d-?\d{7}$/, "מספר נייד שגוי")
      .required("שדה חובה"),
    emailAddress: yup.string().email('דוא"ל שגוי').required("שדה חובה"),
    showRoom: yup.number().positive("יש לבחור אולם תצוגה").defined(),
    terms: yup.mixed().oneOf([true], "יש לאשר את התקנון"),
    approveMailingList: yup.boolean().notRequired(),
  })
  .defined()

const initialValues: yup.InferType<typeof schema> = {
  firstName: "",
  lastName: "",
  mobilePhone: "",
  emailAddress: "",
  showRoom: 0,
  terms: false,
  approveMailingList: false,
}

const OrderPage = () => {
  const bagRef = useRef<FormikProps<typeof initialValues>>()
  const [errorMessage, setErrorMessage] = useState("")
  const [pageInStock, setPageInStock] = useState(true)

  const { userInfo: registeredUserInfo } = useTypedSelector(
    (state) => state.auth
  )
  const router = useRouter()
  const dispatch = useAppDispatch()

  const history = useHistory()
  const flowInStock = useInStock()
  const isInStock = pageInStock && flowInStock

  const { authenticated } = useTypedSelector((state) => state.auth)
  const { initialized } = useTypedSelector((state) => state.wizard)
  const { selectedDetailingCode } = useTypedSelector((state) => state.detailing)
  const { selectedColorCode } = useTypedSelector((state) => state.color)
  const { selectedUpholsteryCode } = useTypedSelector(
    (state) => state.upholstery
  )
  const { selectedAccessoriesCodes } = useTypedSelector(
    (state) => state.accessories
  )

  const firstNameRef = useRef<HTMLInputElement>(null)
  const lastNameRef = useRef<HTMLInputElement>(null)
  const mobilePhoneRef = useRef<HTMLInputElement>(null)
  const emailAddressRef = useRef<HTMLInputElement>(null)

  const termsRef = useRef<HTMLButtonElement>(null)

  const fieldNameInputRefMap = useMemo(
    () => ({
      firstName: firstNameRef,
      lastName: lastNameRef,
      mobilePhone: mobilePhoneRef,
      emailAddress: emailAddressRef,
      terms: termsRef,
    }),
    []
  )

  const createOrder = useCallback(async () => {
    // 1. Create a new order
    const { data } = await postEcommOrders({
      finishingLevelCode: selectedDetailingCode,
      colorCode: selectedColorCode,
      upholsteryCode: selectedUpholsteryCode,
      carExtraCodes: selectedAccessoriesCodes,
    })

    if (data?.errorCode === 90002) {
      dispatch(setOutOfStockComplete(true))
      router.push("/shop/success")
      // orderRepository.clearOrderId()
      return
    }

    if (data?.body?.orderId) {
      // 2. Store the order id
      orderRepository.storeOrderId(data?.body?.orderId)
    } else {
      orderRepository.clearOrderId()
      setPageInStock(false)
    }

    // 3. Return the order id
    return data?.body?.orderId
  }, [
    selectedDetailingCode,
    selectedColorCode,
    selectedUpholsteryCode,
    selectedAccessoriesCodes,
    dispatch,
    router,
  ])

  const areAllComponentsSelected =
    useRedirectIfPreviousComponentsAreNotSelected()

  /**
   * 1. If we have an order id, we already have an order and we don't need to do anything.
   * 2. If we haven't initialized the app, there is nothing to do.
   * 3. If we haven't selected all the components, we can't create an order.
   *
   * If we have an order id or we haven't initialized the app, we don't need to
   * do anything. If we haven't selected all the components, we can't create an
   * order.
   */
  useEffect(() => {
    // 1
    if (
      // 2
      orderRepository.getOrderId() ||
      // 3
      !initialized ||
      !areAllComponentsSelected
    )
      return

    createOrder()
  }, [initialized, areAllComponentsSelected, createOrder])

  // when the component renders for the first time, check if there is a registered user info
  useEffect(() => {
    console.log(registeredUserInfo)
    if (!registeredUserInfo) return

    // if there is a registered user info, set the values of the form fields to the user info
    bagRef.current?.setFieldValue(
      "firstName",
      `${registeredUserInfo.firstName}`
    )
    bagRef.current?.setFieldValue("lastName", `${registeredUserInfo.lastName}`)

    bagRef.current?.setFieldValue("mobilePhone", registeredUserInfo.mobilePhone)
    bagRef.current?.setFieldValue("showRoom", registeredUserInfo.showRoom)
    registeredUserInfo.emailAddress &&
      bagRef.current?.setFieldValue(
        "emailAddress",
        registeredUserInfo.emailAddress
      )
  }, [registeredUserInfo])

  useEffect(() => {
    // Do nothing if the component is not initialized yet
    if (!initialized) return

    // If the user is authenticated, track the checkout step
    if (authenticated) {
      trackCheckoutStep("4")
    } else {
      // If the user is not authenticated, redirect them to the summary page
      history.replace(routes.summary.path)
    }
  }, [initialized, authenticated, history])

  return (
    <>
      <StyledHeadingContainer>
        <StyledImageButton
          onClick={() => {
            history.push(routes.summary.path)
          }}
        >
          <StyledImage src="/icons/forward-arrow.svg" />
        </StyledImageButton>

        <StyledHeading>
          {isInStock ? routes.order.title : "השארת פרטים"}
        </StyledHeading>
      </StyledHeadingContainer>

      <Formik
        innerRef={bagRef as any}
        validationSchema={schema}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (
          {
            firstName,
            lastName,
            mobilePhone,
            emailAddress,
            showRoom,
            approveMailingList,
          },
          { setSubmitting }
        ) => {
          const userInfo = {
            firstName,
            lastName,
            mobilePhone,
            emailAddress,
            showRoom,
            approveMailingList,
          }

          // Get the stored order ID or create a new order and get it's ID
          const orderId = orderRepository.getOrderId() ?? (await createOrder())

          try {
            const { data } = await putEcommOrders({
              orderId,
              finishingLevelCode: selectedDetailingCode,
              colorCode: selectedColorCode,
              upholsteryCode: selectedUpholsteryCode,
              extrasCodes: selectedAccessoriesCodes,
              userInfo,
            })

            if (data?.errorCode === 90002 || !isInStock) {
              dispatch(setOutOfStockComplete(true))
              trackPersonalDetails(
                "Purchase_leed",
                userInfo.emailAddress,
                userInfo.mobilePhone,
                false
              )
              router.push("/shop/success")
              return
            }

            await dispatch(fetchPaymentUrl(orderId))
            history.push(routes.payment.path)
          } catch (error) {
            console.error("Error processing order:", error)
            setErrorMessage("אירעה שגיאה בעת עיבוד ההזמנה. אנא נסה שנית.")
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({ isSubmitting, isValid }) => {
          return (
            <StyledFormContainer>
              <StyledInfo>*שדות חובה</StyledInfo>
              <UseFocusFirstInvalidField
                fieldNameInputRefMap={fieldNameInputRefMap}
              />
              <StyledForm noValidate>
                <StyledInnerContainer>
                  <StyledRow>
                    <StyledField
                      name="firstName"
                      id="firstName"
                      component={TextField}
                      label="שם פרטי"
                      autoComplete="given-name"
                      required
                      helperText=" " // prevent a "jump" when an error appears
                      inputRef={firstNameRef}
                      autoFocus
                      placeholder="שם פרטי"
                    />

                    <ShowRoomSelect showLabel />
                  </StyledRow>

                  <StyledRow>
                    <StyledField
                      name="lastName"
                      id="lastName"
                      component={TextField}
                      label="שם משפחה"
                      autoComplete="family-name"
                      required
                      helperText=" " // prevent a "jump" when an error appears
                      inputRef={lastNameRef}
                      autoFocus
                      placeholder="שם משפחה"
                    />

                    <StyledField
                      name="emailAddress"
                      id="emailAddress"
                      $email={true}
                      component={TextField}
                      type="email"
                      label='דוא"ל'
                      autoComplete="email"
                      required
                      inputProps={{ style: { direction: "ltr" } }}
                      inputRef={emailAddressRef}
                      placeholder="*מייל"
                      classes={{ root: "orderField" }}
                    />
                  </StyledRow>

                  <StyledField
                    name="mobilePhone"
                    id="mobilePhone"
                    component={TextField}
                    type="tel"
                    label="מספר נייד"
                    autoComplete="tel-national"
                    required
                    helperText=" " // prevent a "jump" when an error appears
                    inputProps={{ maxLength: 11, style: { direction: "ltr" } }}
                    inputRef={mobilePhoneRef}
                    classes={{ root: "orderField" }}
                  />
                </StyledInnerContainer>
                <StyledInnerContainer>
                  <FormikCheckboxField
                    name="terms"
                    label={
                      <>
                        הנני מאשר כי קראתי את{" "}
                        <Link
                          href="/מאזדה_אתר_הזמנות_אוןליין_תקנון_2209_G3693-V001.pdf"
                          target="_blank"
                        >
                          תקנון הרכישה
                        </Link>
                      </>
                    }
                    required
                    inputRef={termsRef}
                    classes={{ root: "orderCheckbox" }}
                  />

                  <FormikCheckboxField
                    name="approveMailingList"
                    label="אני רוצה לקבל פניות ועדכונים במייל ו/או בווטסאפ ו/או בסמס מקבוצת דלק מוטורס על מבצעים, השקות, אירועים ועוד. ולהיכלל במאגר המידע של הקבוצה."
                    classes={{ root: "orderCheckbox" }}
                  />

                  <StyledButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !isValid}
                    $disabled={isSubmitting || !isValid}
                    fullWidth
                  >
                    {isInStock ? "שליחת הזמנה" : "שליחת פרטים"}
                  </StyledButton>
                  {errorMessage && <StyledInfo>{errorMessage}</StyledInfo>}
                </StyledInnerContainer>
              </StyledForm>
            </StyledFormContainer>
          )
        }}
      </Formik>
    </>
  )
}

export default OrderPage
